import React, { useState } from "react";
import { Link } from "react-router-dom";
import { BrandMark } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const CATEGORIES = [
  { value: "data_access", label: "I need access to my data" },
  { value: "data_correction", label: "I need to correct my data" },
  { value: "data_deletion", label: "I want my data deleted" },
  { value: "consent_withdrawal", label: "I want to withdraw consent" },
  { value: "unauthorized_processing", label: "I suspect unauthorised processing" },
  { value: "other", label: "Other DPDP concern" },
];

export default function Grievance() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", category: "data_access", subject: "", description: "" });
  const [submitted, setSubmitted] = useState(null);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/grievance", form);
      setSubmitted(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/"><BrandMark /></Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"><ArrowLeft size={14} /> Home</Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
        <div className="overline">DPDP · Grievance redressal</div>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl text-primary leading-tight">File a grievance</h1>
        <p className="mt-4 text-muted-foreground">
          Reach our Data Protection Officer directly. SLA: response within 30 days as required under DPDP §13.
        </p>

        {submitted ? (
          <div className="mt-10 rounded-lg border border-secondary/40 bg-secondary/8 p-8" data-testid="grievance-success">
            <CheckCircle2 className="text-secondary" size={28} />
            <h2 className="mt-4 font-serif text-3xl text-primary">Grievance recorded</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Ticket <span className="font-mono text-primary" data-testid="grievance-ticket">{submitted.ticket_id}</span> has been assigned to
              <strong> {submitted.grievance_officer}</strong>. We will respond within {submitted.response_sla_days} days at
              <a href={`mailto:${submitted.contact}`} className="text-primary underline"> {submitted.contact}</a>.
            </p>
            <Link to="/" className="mt-6 inline-block">
              <Button variant="outline" data-testid="grievance-back-home">Back to home</Button>
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 rounded-lg border border-border bg-white p-6 lg:p-8 space-y-4" data-testid="grievance-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="grv-name">Your name</Label>
                <Input id="grv-name" required value={form.name} onChange={set("name")} data-testid="grievance-name-input" />
              </div>
              <div>
                <Label htmlFor="grv-email">Email</Label>
                <Input id="grv-email" type="email" required value={form.email} onChange={set("email")} data-testid="grievance-email-input" />
              </div>
              <div>
                <Label htmlFor="grv-phone">Phone (optional)</Label>
                <Input id="grv-phone" value={form.phone} onChange={set("phone")} data-testid="grievance-phone-input" />
              </div>
              <div>
                <Label htmlFor="grv-category">Category</Label>
                <Select value={form.category} onValueChange={set("category")}>
                  <SelectTrigger id="grv-category" data-testid="grievance-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="grv-subject">Subject</Label>
              <Input id="grv-subject" required value={form.subject} onChange={set("subject")} placeholder="Short summary" data-testid="grievance-subject-input" />
            </div>
            <div>
              <Label htmlFor="grv-desc">Describe your concern</Label>
              <Textarea id="grv-desc" required rows={6} value={form.description} onChange={set("description")} data-testid="grievance-description-input" />
            </div>
            <Button type="submit" disabled={saving} className="w-full h-11" data-testid="grievance-submit-button">
              {saving ? "Submitting…" : "Submit to DPO"}
            </Button>
            <p className="text-xs text-muted-foreground">
              By submitting you consent to the processing of the details above for the purpose of grievance handling only.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
